import React, { useEffect, useRef, useState } from "react";
import { MdAutorenew } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { clientActions } from "../../actions/client.actions";
import Utils from "../../utils";
import { clientConstants } from "../../constants/client.constants";
import Title from "../common/Title";
import { GoOrganization } from "react-icons/go";
import EnhancedGrid from "../common/EnhancedGrid";
import ConfirmModal from "../common/ConfirmModal";
import GridHelper from "../helpers/gridHelper";
import { FaKey, FaBuilding, FaBan, FaCheckCircle, FaSearch } from "react-icons/fa";
import { Button, Form, InputGroup, Badge } from "react-bootstrap";
import { usePermissions } from "../../hooks/usePermissions";
import { useToast } from "../../contexts/ToastContext";

// Matches what EnhancedGrid itself sends as order_by for the "Expires On" column header
// (Utils.fixCamelCase("expiresOn") -> "Expires On" -> spaces stripped) — see its onSort.
const DEFAULT_ORDER_COLUMN = "ExpiresOn";

function ClientGrid(props) {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const client = useSelector((state) => state.client);
  const { isSuperUser, isProductTeam } = usePermissions(props.user);
  const { showToast } = useToast();

  // One combined box searches both name and ID at once (server-side, same param set to
  // both — mirrors the client switcher in the header, ClientMenu.jsx) rather than two
  // separate fields in a collapsible filter panel.
  const [searchText, setSearchText] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active"); // "active" | "inactive" | "all"
  const [showSetActiveConfirm, setShowSetActiveConfirm] = useState(false);
  const [setActivePending, setSetActivePending] = useState(false);
  const isFirstSearchRender = useRef(true);

  const fetchClients = () => {
    let queryParams = Utils.getQueryStringParams(location);
    // Server-side tri-state filter: "active" | "inactive" | "all".
    queryParams.status = statusFilter;
    // Show a large page by default so the list rarely needs paging.
    queryParams.pageSize = queryParams.pageSize || 100;
    // Default to soonest-expiring-first so renewals due soon surface immediately; only
    // applied when the user hasn't picked a sort via a column header.
    if (!queryParams.order_by) {
      queryParams.order_by = DEFAULT_ORDER_COLUMN;
      queryParams.order_direction = "Asc";
    }

    dispatch(
      clientActions.get(queryParams, clientConstants.CALLER_CLIENT_GRID)
    );

    // Initialize the search box from the URL (name and ID are always set together — see
    // the debounce effect below — so either one reflects the active search text).
    setSearchText(queryParams["clientName"] || queryParams["clientId"] || "");
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line
  }, [location, statusFilter, props.refreshSignal]);

  // Live search, debounced — same feel as the client switcher in the header. Skipped on the
  // very first render since `searchText` there just mirrors what fetchClients already read
  // from the URL; searching again with the identical value would be a pointless extra fetch.
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      GridHelper.handleSearch({ clientName: searchText, clientId: searchText }, location, navigate);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line
  }, [searchText]);

  // After a successful enable/disable, refresh the list and notify the user.
  useEffect(() => {
    if (setActivePending && client.success) {
      showToast(
        `Client ${selectedClient?.active ? "deactivated" : "reactivated"} successfully`,
        "success"
      );
      setSetActivePending(false);
      setShowSetActiveConfirm(false);
      setSelectedClient(null);
      fetchClients();
    }
    if (setActivePending && client.error) {
      showToast("Unable to update client status. Please try again.", "danger");
      setSetActivePending(false);
      setShowSetActiveConfirm(false);
    }
    // eslint-disable-next-line
  }, [client.success, client.error]);

  const handleConfirmSetActive = () => {
    if (!selectedClient) return;
    setSetActivePending(true);
    dispatch(
      clientActions.setActive({
        clientId: selectedClient.clientId,
        clientGuid: selectedClient.clientGuid,
        active: !selectedClient.active,
      })
    );
  };

  let displayColumns = ["clientId", "clientName", "status", "expiresOn"];

  // Seeds the column header's sort arrow to match the default (or user-chosen) sort, the
  // same way AzureCostGrid does for its own default-sort column.
  const urlParams = Utils.getQueryStringParams(location);
  const activeOrderBy = urlParams.order_by || DEFAULT_ORDER_COLUMN;
  const activeOrderDirection = (urlParams.order_direction || "Asc") === "Asc" ? 1 : -1;

  const data = (client.paginatedResultGrid?.data ?? []).map((c) => {
    return {
      ...c,
      expiresOn: Utils.convertToDateOnly(c.expiresOn),
      status: c.active ? (
        <Badge bg="success">Active</Badge>
      ) : (
        <Badge bg="secondary">Inactive</Badge>
      ),
    };
  });

  return (
    <>
      {!props.hideTitle && <Title text="Clients" icon={<GoOrganization className="mb-1" />} />}

      {/* Action button bar */}
      <div
        className="d-flex gap-2 mb-3 p-3 bg-light rounded align-items-center"
        style={{
          opacity: selectedClient ? 1 : 0.6,
          transition: "opacity 0.3s ease",
        }}
      >
        {isProductTeam && (
          <Button
            variant="outline-primary"
            size="sm"
            disabled={!selectedClient}
            onClick={() =>
              props.onRenewClient
                ? props.onRenewClient(selectedClient?.clientId)
                : navigate(`renew?clientId=${selectedClient?.clientId}`)
            }
          >
            <MdAutorenew className="mb-1 me-2" />
            Renew
          </Button>
        )}
        {isProductTeam && (
          <Button
            variant={
              selectedClient?.active ? "outline-danger" : "outline-success"
            }
            size="sm"
            disabled={!selectedClient}
            onClick={() => setShowSetActiveConfirm(true)}
          >
            {selectedClient?.active ? (
              <>
                <FaBan className="mb-1 me-2" />
                Deactivate
              </>
            ) : (
              <>
                <FaCheckCircle className="mb-1 me-2" />
                Reactivate
              </>
            )}
          </Button>
        )}
        {isSuperUser && (
          <Button
            variant="outline-primary"
            size="sm"
            disabled={!selectedClient}
            onClick={() =>
              props.onManageAuthenticators
                ? props.onManageAuthenticators(selectedClient?.clientId)
                : navigate(`authenticators?clientId=${selectedClient?.clientId}`)
            }
          >
            <FaKey className="mb-1 me-2" />
            Authenticators
          </Button>
        )}
        {isSuperUser && (
          <Button
            variant="primary"
            size="sm"
            className="ms-auto"
            onClick={() => (props.onAddClient ? props.onAddClient() : navigate("add"))}
          >
            Add Client
          </Button>
        )}
        {selectedClient && (
          <div className={isSuperUser ? "align-self-center" : "ms-auto align-self-center"}>
            <small className="text-muted">
              Selected: <strong>{selectedClient.clientName}</strong>
            </small>
          </div>
        )}
      </div>

      {/* Search + status filter */}
      <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
        <InputGroup size="sm" style={{ maxWidth: 320 }}>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search by client name or ID"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </InputGroup>
        <div className="d-flex gap-3">
          {[
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
            { value: "all", label: "All" },
          ].map((option) => (
            <Form.Check
              key={option.value}
              type="checkbox"
              id={`client-status-${option.value}`}
              label={option.label}
              checked={statusFilter === option.value}
              onChange={() => {
                setStatusFilter(option.value);
                setSelectedClient(null);
              }}
            />
          ))}
        </div>
      </div>

      <EnhancedGrid
        gridId="client"
        fillHeight={props.hideTitle}
        data={data}
        displayColumns={displayColumns}
        emptyState={{
          icon: <FaBuilding size={80} className="text-muted mb-3" />,
          message: "No clients found",
          subtitle: "Clients will appear here once they are added to the system.",
          action: isSuperUser ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => (props.onAddClient ? props.onAddClient() : navigate("add"))}
            >
              Add Client
            </Button>
          ) : null,
        }}
        idColumn={"clientId"}
        maxHeight="calc(100vh - 330px)"
        initialOrderBy={activeOrderBy}
        initialOrderDirection={activeOrderDirection}
        onRowClick={setSelectedClient}
        selectedRow={selectedClient}
        pageNumber={(client.paginatedResultGrid?.pageNumber ?? 1) - 1}
        totalPages={client.paginatedResultGrid?.totalPages ?? 0}
        loading={client.loadingGrid}
        onPageChange={GridHelper.handlePageClick}
        onSort={GridHelper.handleSortClick}
      />

      <ConfirmModal
        show={showSetActiveConfirm}
        handleClose={() => setShowSetActiveConfirm(false)}
        handleConfirm={handleConfirmSetActive}
        title={`${selectedClient?.active ? "Deactivate" : "Reactivate"} Client`}
        question={
          <>
            Are you sure you want to{" "}
            {selectedClient?.active ? "deactivate" : "reactivate"}{" "}
            <strong>{selectedClient?.clientName}</strong>?
          </>
        }
        yesButtonText={selectedClient?.active ? "Deactivate" : "Reactivate"}
        noButtonText="Cancel"
      />
    </>
  );
}

export default ClientGrid;
